/** Same-origin calls to the sign-in API (/api/auth/*). The session lives in an HttpOnly cookie. */
import { forgetPromoCode, storedPromoCode } from './referrals';

export interface Account {
  id: string;
  role: string;
  name: string;
  username: string | null;
  photo_url: string | null;
}

export interface AuthConfig {
  telegram_bot: string | null;
  telegram_bot_id: number | null;
  password_login: boolean;
  dev_login: boolean;
}

/** The object the Telegram Login Widget passes to its callback. */
export type TelegramWidgetUser = Record<string, string | number>;

export class AuthError extends Error {
  readonly status: number;
  /** Machine-readable reason from the API (`detail`), e.g. "wrong_credentials". */
  readonly code: string | null;

  constructor(status: number, code: string | null = null) {
    super(`auth request failed with HTTP ${String(status)}`);
    this.status = status;
    this.code = code;
  }
}

async function errorCode(response: Response): Promise<string | null> {
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null && 'detail' in body) {
      return typeof body.detail === 'string' ? body.detail : null;
    }
  } catch {
    // not JSON
  }
  return null;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');
  const response = await fetch(path, { credentials: 'same-origin', ...init, headers });
  if (!response.ok) throw new AuthError(response.status, await errorCode(response));
  return (await response.json()) as T;
}

const post = (body?: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: body === undefined ? undefined : JSON.stringify(body),
});

/** The signed-in account, or null when there is no valid session. */
export async function fetchAccount(): Promise<Account | null> {
  try {
    return (await call<{ user: Account }>('/api/auth/me')).user;
  } catch (error) {
    if (error instanceof AuthError && error.status === 401) return null;
    throw error;
  }
}

export const fetchAuthConfig = () => call<AuthConfig>('/api/auth/config');

/**
 * A sign-in that carries the promo code kept in this browser, if any. The account joins it if it
 * still can (no code yet, no top-up); either way the code has done its job and is forgotten.
 */
async function signIn(path: string, body: Record<string, unknown>): Promise<Account> {
  const code = storedPromoCode();
  const { user } = await call<{ user: Account }>(
    path,
    post(code === null ? body : { ...body, promo_code: code }),
  );
  forgetPromoCode();
  return user;
}

export const signInWithTelegram = (user: TelegramWidgetUser) => signIn('/api/auth/telegram', user);

export const signInWithPassword = (email: string, password: string) =>
  signIn('/api/auth/login', { email, password });

export const registerWithPassword = (email: string, password: string) =>
  signIn('/api/auth/register', { email, password });

export async function signInAsDevUser(): Promise<Account> {
  return (await call<{ user: Account }>('/api/auth/dev', post())).user;
}

export async function signOut(): Promise<void> {
  const response = await fetch('/api/auth/logout', { ...post(), credentials: 'same-origin' });
  if (!response.ok && response.status !== 401) throw new AuthError(response.status);
}
